import { useCallback, useEffect, useState } from "react";

export const useLocalStorage = <T>(
  key: string,
  initialValue: T
): { localData: T; setItem: (data: T) => void; removeItem: () => void } => {
  const [localData, setLocalData] = useState<T>(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(
        `Failed to parse localStorage value for key "${key}":`,
        error
      );
      return initialValue;
    }
  });

  useEffect(() => {
    const item = localStorage.getItem(key);
    if (!item && initialValue !== undefined) {
      try {
        localStorage.setItem(key, JSON.stringify(initialValue));
      } catch (error) {
        console.warn(`Failed to set initial value in localStorage:`, error);
      }
    }
  }, [key, initialValue]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key) {
        try {
          const newValue = e.newValue ? JSON.parse(e.newValue) : initialValue;
          setLocalData(newValue);
        } catch (error) {
          console.warn(`Failed to sync localStorage change:`, error);
          setLocalData(initialValue);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [key, initialValue]);

  const setItem = useCallback(
    (data: T) => {
      try {
        localStorage.setItem(key, JSON.stringify(data));
        setLocalData(data);
      } catch (error) {
        console.warn(`Failed to save to localStorage:`, error);
      }
    },
    [key]
  );

  const removeItem = useCallback(() => {
    try {
      localStorage.removeItem(key);
      setLocalData(initialValue);
    } catch (error) {
      console.warn(`Failed to remove from localStorage:`, error);
    }
  }, [key, initialValue]);

  return { localData, setItem, removeItem };
};
